/**
 * KanbanView.jsx
 * Vista Kanban reutilizable para mostrar items por columnas de estado
 */
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";

const defaultColumns = [
  { id: 'pendiente', label: 'Pendiente', color: 'bg-yellow-500' },
  { id: 'en_proceso', label: 'En Proceso', color: 'bg-blue-500' },
  { id: 'completado', label: 'Completado', color: 'bg-green-500' },
];

export default function KanbanView({ 
  items = [], 
  columns = defaultColumns,
  statusField = 'estado',
  renderCard,
  onItemClick
}) {
  // Group items by status
  const groupedItems = columns.reduce((acc, col) => {
    acc[col.id] = items.filter(item => {
      const status = item[statusField]?.toLowerCase().replace(/\s+/g, '_');
      // Handle multiple status mappings
      if (col.id === 'pendiente') {
        return status === 'pendiente' || status === 'abierto';
      }
      if (col.id === 'en_proceso') {
        return status === 'en_proceso' || status === 'en_progreso';
      }
      if (col.id === 'completado') {
        return status === 'completado' || status === 'resuelto' || status === 'cerrado';
      }
      return status === col.id;
    });
    return acc;
  }, {});

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-300px)] min-h-[500px]">
      {columns.map(column => (
        <div key={column.id} className="flex flex-col bg-slate-50 dark:bg-slate-800/50 rounded-lg">
          {/* Column Header */}
          <div className="flex items-center justify-between p-3 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${column.color}`} />
              <span className="font-medium text-slate-900 dark:text-white">{column.label}</span>
            </div>
            <Badge variant="secondary" className="bg-slate-200 dark:bg-slate-700">
              {groupedItems[column.id]?.length || 0}
            </Badge>
          </div>
          
          {/* Column Content */}
          <ScrollArea className="flex-1 p-2">
            <div className="space-y-2">
              {groupedItems[column.id]?.map((item, idx) => (
                <Card 
                  key={item.id || idx} 
                  className="cursor-pointer hover:shadow-md transition-shadow bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                  onClick={() => onItemClick?.(item)}
                >
                  <CardContent className="p-3">
                    {renderCard ? renderCard(item) : (
                      <div>
                        <p className="font-medium text-sm text-slate-900 dark:text-white">
                          {item.titulo || item.cliente || item.name || 'Sin título'}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {item.descripcion?.slice(0, 50) || item.notas?.slice(0, 50) || ''}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              
              {(!groupedItems[column.id] || groupedItems[column.id].length === 0) && (
                <div className="text-center py-8 text-slate-400 text-sm">
                  No hay items
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      ))}
    </div>
  );
}

export { defaultColumns };
